import * as React from 'react';
import { Link } from 'react-router-dom';
import { AuthDescription } from '../AuthDescription';
import { AuthForm } from '../AuthForm';
import { AuthFormError } from '../AuthFormError';
import { AuthFormRow } from '../AuthFormRow';
import { AuthHeading } from '../AuthHeading';
import {
  AuthRedirectMessage,
  AuthRedirectMessageType
} from '../AuthRedirectMessage';
import { Button } from '../../Forms/Button';
import { Checkbox } from '../../Forms/Checkbox';
import { FormRow } from '../../Forms/FormRow';
import { debounce, Debounced } from '../../../utils/debounce';
import { validateEmail } from '../../../validators/email';
import { validateFullName } from '../../../validators/name';
import { validatePasswordWithConfirm } from '../../../validators/password';
import './style.css';

export interface SignUpProps {
  error: boolean | string;
  loading: boolean;
  onSignUp: (
    email: string,
    password: string,
    name: string,
    contactPreference: boolean
  ) => void;
}

export interface SignUpState {
  confirmPassword: string;
  contactPreference: boolean;
  email: string;
  emailError?: string;
  name: string;
  nameError?: string;
  password: string;
  passwordError?: string;
  termsAndConditionsAgreed: boolean;
  termsAndConditionsError?: string;
}

export const GENERATED_PASSWORD_LENGTH = 16;

export class SignUp extends React.Component<SignUpProps, SignUpState> {
  private debouncedValidateEmail: Debounced<(email?: string) => void>;

  constructor(props: SignUpProps) {
    super(props);

    this.state = {
      confirmPassword: '',
      contactPreference: false,
      email: '',
      name: '',
      password: '',
      termsAndConditionsAgreed: false
    };

    // Debounce to prevent rapid revalidation as user types
    this.debouncedValidateEmail = debounce(this.validateEmail, 200);
  }

  componentWillUnmount() {
    this.debouncedValidateEmail.cancel();
  }

  render() {
    const { error, loading } = this.props;
    const {
      confirmPassword,
      contactPreference,
      email,
      emailError,
      name,
      nameError,
      password,
      passwordError,
      termsAndConditionsAgreed,
      termsAndConditionsError
    } = this.state;

    return (
      <main className="sign-up">
        <AuthHeading>Create your Edge of Play account</AuthHeading>
        <AuthDescription>
          Bringing cutting edge coaching material for the sports you love right
          to your fingertips.
          <br />
          Sign up now to keep track of your favourite content and get in touch
          with the community.
        </AuthDescription>
        <AuthForm onSubmit={this.trySignUp}>
          <AuthFormRow
            className="sign-up-form-row"
            inputClassName="sign-up-input"
            label="Your Name:"
            autoComplete="name"
            autoFocus={true}
            value={name}
            error={nameError}
            onChange={event =>
              this.updateName((event.target as HTMLInputElement).value)
            }
          />
          <AuthFormRow
            className="sign-up-form-row"
            inputClassName="sign-up-input"
            label="Your Email:"
            autoComplete="email"
            value={email}
            error={emailError}
            onChange={event =>
              this.updateEmail((event.target as HTMLInputElement).value)
            }
          />
          <AuthFormRow
            className="reset-password-form-row"
            inputClassName="reset-password-input"
            label="Password:"
            autoComplete="new-password"
            type="password"
            value={password}
            error={passwordError}
            onChange={event =>
              this.updatePassword((event.target as HTMLInputElement).value)
            }
          />
          <AuthFormRow
            className="reset-password-form-row"
            inputClassName="reset-password-input"
            label="Confirm Password:"
            type="password"
            value={confirmPassword}
            onChange={event =>
              this.updateConfirmPassword(
                (event.target as HTMLInputElement).value
              )
            }
          />
          {/* TODO: Honeypot input? Fake password confirm is a nice candidate with this setup */}
          <AuthFormError
            className="edge-of-play-form-row sign-up-error"
            error={
              error
                ? typeof error === 'string'
                  ? error
                  : `Sign up failed, please check the details entered and try again`
                : undefined
            }
          />
          <FormRow className="sign-up-terms-and-conditions-row">
            <label className="sign-up-terms-and-conditions-label">
              <Checkbox
                className="sign-up-terms-and-conditions-checkbox"
                checked={termsAndConditionsAgreed}
                onChange={event =>
                  this.updateTermsAndConditionsAgreed(
                    event.currentTarget.checked
                  )
                }
              />
              <span className="sign-up-terms-and-conditions">
                I have read and agreed with the Edge of Play{' '}
                <Link
                  to="/terms"
                  target="_blank"
                  className="edge-of-play-link terms-and-conditions-link"
                >
                  terms and conditions
                </Link>{' '}
                for using this website
              </span>
            </label>
          </FormRow>
          <AuthFormError
            className="edge-of-play-form-row sign-up-error"
            error={termsAndConditionsError}
          />
          <FormRow className="sign-up-contact-preference-row">
            <label className="sign-up-contact-preference-label">
              <Checkbox
                className="sign-up-contact-preference-checkbox"
                checked={contactPreference}
                onChange={event =>
                  this.updateContactPreference(event.currentTarget.checked)
                }
              />
              <span className="sign-up-contact-preference">
                I am happy for Edge of Play to contact me on the above e-mail
                address
              </span>
            </label>
          </FormRow>
          <FormRow>
            <Button className="sign-up-button" type="submit" disabled={loading}>
              Sign Up
            </Button>
          </FormRow>
          <FormRow>
            <AuthRedirectMessage
              type={AuthRedirectMessageType.RedirectToLogin}
            />
          </FormRow>
        </AuthForm>
      </main>
    );
  }

  private shouldSubmitBeDisabled = (): boolean => {
    const { loading } = this.props;
    const {
      email,
      emailError,
      name,
      nameError,
      termsAndConditionsAgreed
    } = this.state;

    return !!(
      loading ||
      !termsAndConditionsAgreed ||
      !email ||
      !name ||
      emailError ||
      nameError
    );
  };

  private updateName = (name: string): void => {
    this.validateName(name);
    this.setState({
      name
    });
  };

  private updateEmail = (email: string): void => {
    this.debouncedValidateEmail(email);
    this.setState({
      email
    });
  };

  private updatePassword = (password: string): void => {
    this.validatePassword(password, this.state.confirmPassword);
    this.setState({
      password
    });
  };

  private updateConfirmPassword = (confirmPassword: string): void => {
    this.validatePassword(this.state.password, confirmPassword);
    this.setState({
      confirmPassword
    });
  };

  private updateTermsAndConditionsAgreed = (
    termsAndConditionsAgreed: boolean
  ): void => {
    this.validateTermsAndConditionsAgreed(termsAndConditionsAgreed);
    this.setState({
      termsAndConditionsAgreed
    });
  };

  private updateContactPreference = (contactPreference: boolean): void => {
    this.setState({
      contactPreference
    });
  };

  private validateName = (name: string): void => {
    this.setState({
      nameError: validateFullName(name)
    });
  };

  private validateEmail = (email: string): void => {
    this.setState({
      emailError: validateEmail(email)
    });
  };

  private validatePassword = (
    password: string,
    confirmPassword: string
  ): void => {
    this.setState({
      passwordError: validatePasswordWithConfirm(password, confirmPassword)
    });
  };

  private validateTermsAndConditionsAgreed = (
    termsAndConditionsAgreed: boolean
  ): void => {
    this.setState({
      termsAndConditionsError: termsAndConditionsAgreed
        ? undefined
        : `You must agree to the site's terms and conditions to sign up`
    });
  };

  private trySignUp = (event: React.FormEvent<HTMLFormElement>) => {
    const { onSignUp } = this.props;
    const {
      confirmPassword,
      contactPreference,
      email,
      name,
      password,
      termsAndConditionsAgreed
    } = this.state;

    event.preventDefault();

    // Force validation before we check if we can submit
    this.validateName(name);
    this.validateEmail(email);
    this.validatePassword(password, confirmPassword);
    this.validateTermsAndConditionsAgreed(termsAndConditionsAgreed);

    if (onSignUp && !this.shouldSubmitBeDisabled()) {
      onSignUp(email as string, password as string, name, contactPreference);
    }
  };
}



// WEBPACK FOOTER //
// ./src/app/components/Auth/SignUp/index.tsx